import { REQUIREMENT_INTAKE_MODE_KEY } from './mode-contract.js';
import {
  collectComposerAttachmentLifecycleRefs,
  collectReadyAttachmentRefs,
  collectSendableComposerAttachments,
} from './attachment-send-refs.js';

export function isSourcingRefreshCommand(value) {
  void value;
  return false;
}

function normalizeRecord(value, fallback = {}) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return fallback;
  }
  return value;
}

function firstTruthyTextValue(...values) {
  const value = values.find(Boolean);
  return String(value || '').trim();
}

function applyActiveTargetTabs(payloadExtra, activeTargetContext, activeTargetRef) {
  const activeTargetTab = firstTruthyTextValue(
    activeTargetRef?.active_tab,
    activeTargetContext?.active_tab,
  );
  const targetTabs = Array.isArray(payloadExtra.target_tabs) ? payloadExtra.target_tabs : [];
  if (activeTargetTab && targetTabs.length === 0) {
    payloadExtra.target_tabs = [activeTargetTab];
  }
}

function applyActiveArtifactPayload(payloadExtra, activeArtifactTarget) {
  const activeTarget = normalizeRecord(activeArtifactTarget);
  const activeTargetContext = normalizeRecord(activeTarget.context, null);
  const activeTargetRef = normalizeRecord(activeTarget.targetRef, null);
  if (activeTargetContext && !payloadExtra.active_artifact_context) {
    payloadExtra.active_artifact_context = activeTargetContext;
  }
  if (activeTargetRef && !payloadExtra.target_artifact_ref) {
    payloadExtra.target_artifact_ref = activeTargetRef;
  }
  applyActiveTargetTabs(payloadExtra, activeTargetContext, activeTargetRef);
}

function applySavedCanvasPayload(payloadExtra, canvasContent, normalizedModeKey) {
  if (!payloadExtra.active_artifact_context && canvasContent) {
    payloadExtra.active_artifact_context = {
      artifact_id: 'client_saved_canvas',
      document_kind: 'canvas_manual_edit',
      title: '当前 Canvas 文档',
      summary: canvasContent,
      content: canvasContent,
      content_format: 'markdown',
      active_tab: 'requirement',
      status: 'accepted',
      source: 'client_saved_canvas_content',
    };
  }
  if (normalizedModeKey === REQUIREMENT_INTAKE_MODE_KEY && canvasContent) {
    payloadExtra.canvas_content = canvasContent;
  }
}

export function resolveSourcingPayloadExtra({
  streamOptions,
  activeArtifactTarget = null,
  isSourcingMode,
  currentInput,
  latestSourcingQuery,
  latestSourcingResultIds,
  normalizedModeKey = '',
  savedIntakeCanvasContent = '',
}) {
  void isSourcingMode;
  void currentInput;
  void latestSourcingQuery;
  void latestSourcingResultIds;
  const payloadExtra = { ...normalizeRecord(streamOptions?.payloadExtra) };
  const resolvedIntakeCanvasContent = String(savedIntakeCanvasContent || '').trim();
  applyActiveArtifactPayload(payloadExtra, activeArtifactTarget);
  applySavedCanvasPayload(payloadExtra, resolvedIntakeCanvasContent, normalizedModeKey);
  return payloadExtra;
}

export function resolveOutboundContent({
  optionsContent = '',
  currentInput = '',
} = {}) {
  const explicitContent = String(optionsContent ?? '').trim();
  if (explicitContent) {
    return explicitContent;
  }
  return String(currentInput || '').trim();
}

export function resolveSourceGroundingMessage({
  outboundContent = '',
  currentInput = '',
} = {}) {
  const resolvedOutboundContent = String(outboundContent || '').trim();
  if (resolvedOutboundContent) {
    return resolvedOutboundContent;
  }
  return String(currentInput || '').trim();
}

export function resolveSendAttachmentRefs({
  currentComposerFileList = [],
  readyAttachmentRefs = [],
  includeSourceLifecycleRefs = false,
} = {}) {
  if (Array.isArray(currentComposerFileList)) {
    const sendableAttachmentSnapshot = collectSendableComposerAttachments(currentComposerFileList);
    return includeSourceLifecycleRefs
      ? collectComposerAttachmentLifecycleRefs(sendableAttachmentSnapshot)
      : collectReadyAttachmentRefs(sendableAttachmentSnapshot);
  }
  return Array.isArray(readyAttachmentRefs) ? readyAttachmentRefs : [];
}

export function resolveSendAttachmentPayloadRefs({
  currentComposerFileList = [],
  readyAttachmentRefs = [],
  includeSourceLifecycleRefs = false,
} = {}) {
  const hasComposerSnapshot = Array.isArray(currentComposerFileList);
  const sendableAttachmentSnapshot = hasComposerSnapshot
    ? collectSendableComposerAttachments(currentComposerFileList)
    : [];
  const attachmentSnapshotForRefs = hasComposerSnapshot
    ? sendableAttachmentSnapshot
    : currentComposerFileList;
  const contextAttachmentRefs = resolveSendAttachmentRefs({
    currentComposerFileList: attachmentSnapshotForRefs,
    readyAttachmentRefs,
  });
  const streamAttachmentRefs = includeSourceLifecycleRefs
    ? resolveSendAttachmentRefs({
      currentComposerFileList: attachmentSnapshotForRefs,
      readyAttachmentRefs,
      includeSourceLifecycleRefs: true,
    })
    : contextAttachmentRefs;
  return {
    contextAttachmentRefs,
    sendableAttachmentSnapshot,
    streamAttachmentRefs,
  };
}
